import { EnhancedDialog } from 'components/enhanced';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'shared/utils/toast';

interface UpdateThresholdsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedRows: any[];
  onSuccess?: () => void;
}

interface ThresholdPayload {
  product: any;
  warehouse: any;
  understock_threshold: number;
  restock_threshold: number;
  overstock_threshold: number;
}

interface ChangeReport {
  productName: string;
  warehouseName: string;
  variations: Array<{
    label: string;
    oldVal: number;
    newVal: number;
    isUp: boolean;
  }>;
}

/**
 * Dialog Modal Component orchestrating automated stock-threshold calculations.
 *
 * This component runs algorithmic analysis over historical product averages to calculate optimal warehouse levels.
 * It intercepts current data structures, calculates three-tier defensive stock markers, builds a side-by-side delta visualization,
 * and handles transactional bulk updates to remote APIs.
 *
 * **Algorithmic Threshold Formulas Implemented (Ceiling approximation applied at the very end):**
 * 1. **Understock (Minimum Safety stock):** `Math.ceil(average_daily_consumption)`. Clamped to a minimum of 1.
 * 2. **Restock Point (Reorder Checkpoint):** `Math.ceil(average_daily_consumption * 1.5)`. Clamped to a minimum of 1.
 * 3. **Overstock (Maximum Operational Ceiling):** `Math.ceil(average_daily_consumption * 3)`. Clamped to a minimum of 1.
 *
 * @component
 * @param {UpdateThresholdsDialogProps} props - Control parameters steering the data processing dialog.
 * @returns {JSX.Element} An overlay modal container detailing threshold adjustment metrics.
 */
export const UpdateThresholdsDialog = ({
  open,
  onClose,
  selectedRows,
  onSuccess
}: UpdateThresholdsDialogProps) => {
  const { t } = useTranslation();
  const [calculatedThresholds, setCalculatedThresholds] = useState<ThresholdPayload[]>([]);
  const [dialogChangesReport, setDialogChangesReport] = useState<ChangeReport[]>([]);

  useEffect(() => {
    if (!open) return;

    const changesReport: ChangeReport[] = [];
    const newThresholds = selectedRows.map((row) => {
      const avgConsumption = Math.abs(row.avg) || 0;

      // Se un prodotto ha media 687.5,
      // la scorta minima è 687.5 approssimato a 688,
      // il punto di ripristino è 687.5*1.5=1031.25 approssimato a 1032
      // e la scorta massima è 687.5*3=2062.5 approssimato a 2063

      // Scorta minima: consumo medio con arrotondamento per eccesso (minimo 1)
      const understock = Math.ceil(avgConsumption) || 1;

      // Punto di riordino: consumo medio * 1.5 (minimo 1)
      const restock = Math.ceil(avgConsumption * 1.5) || 1;

      // Scorta massima: consumo medio per 3 (minimo 1)
      const overstock = Math.ceil(avgConsumption * 3) || 1;

      const details = [];

      if (understock !== row.understock_threshold) {
        details.push({
          label: 'Sotto-scorta',
          oldVal: row.understock_threshold,
          newVal: understock,
          isUp: understock > row.understock_threshold
        });
      }
      if (restock !== row.restock_threshold) {
        details.push({
          label: 'Riassortimento',
          oldVal: row.restock_threshold,
          newVal: restock,
          isUp: restock > row.restock_threshold
        });
      }
      if (overstock !== row.overstock_threshold) {
        details.push({
          label: 'Sovra-scorta',
          oldVal: row.overstock_threshold,
          newVal: overstock,
          isUp: overstock > row.overstock_threshold
        });
      }

      if (details.length > 0) {
        changesReport.push({
          productName: row.product_name || row.product,
          warehouseName: row.warehouse_name || row.warehouse,
          variations: details
        });
      }

      return {
        product: row.product,
        warehouse: row.warehouse,
        understock_threshold: understock,
        restock_threshold: restock,
        overstock_threshold: overstock
      };
    });

    setCalculatedThresholds(newThresholds);
    setDialogChangesReport(changesReport);
  }, [open, selectedRows]);

  const handleConfirmUpdate = () => {
    if (dialogChangesReport.length === 0) {
      onClose();
      return;
    }

    productWarehouseService
      .updateThresholds(calculatedThresholds)
      .then(() => {
        toast.success('Soglie aggiornate e salvate con successo.');
        if (onSuccess) onSuccess();
        onClose();
      })
      .catch((error) => {
        console.error(error);
        toast.error('Errore durante il salvataggio delle soglie');
      });
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      title="Conferma Aggiornamento Soglie"
      subTitle={
        dialogChangesReport.length === 0
          ? 'Tutte le soglie sono già ottimizzate. Nessuna variazione rilevata.'
          : "L'algoritmo ha calcolato le seguenti variazioni. Vuoi applicarle ai magazzini?"
      }
      submitLabel={dialogChangesReport.length === 0 ? 'Chiudi' : 'Conferma e Salva'}
      cancelLabel={dialogChangesReport.length === 0 ? '' : t('actions.reset', 'Annulla')}
      isValid={true}
      onSubmit={handleConfirmUpdate}
      maxWidth="md">
      <ul className="flex flex-col gap-3 p-0 m-0 list-none">
        {dialogChangesReport.map((item, index) => (
          <li
            key={index}
            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm hover:border-gray-200 transition-all flex flex-col gap-2">
            {/* Intestazione Prodotto */}
            <div className="flex items-center justify-between border-b border-gray-50 pb-1.5">
              <span className="font-semibold text-gray-900 text-sm">{item.productName}</span>
              {item.warehouseName && (
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-50 px-2 py-0.5 rounded-md">
                  {item.warehouseName}
                </span>
              )}
            </div>

            {/* Elenco Variazioni con Indicatori Grafici */}
            <div className="flex flex-wrap gap-2 pt-1">
              {item.variations.map((v, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-1 text-xs">
                  <span className="font-medium text-gray-500">{v.label}:</span>
                  <span className="font-mono text-gray-400 line-through">{v.oldVal}</span>

                  {/* Freccia colorata in base alla direzione del cambiamento */}
                  <span className={`font-bold ${v.isUp ? 'text-emerald-500' : 'text-amber-500'}`}>
                    {v.isUp ? '➔' : '➔'}
                  </span>

                  <span
                    className={`font-bold font-mono ${
                      v.isUp ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                    {v.newVal}
                  </span>
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </EnhancedDialog>
  );
};
